import type { ComponentPropsWithoutRef } from 'react';
import type { MenuItemData, SelectNextProps } from '@zs-nimbus/core';
import { FreaFile, FreaFileStatus } from './types';

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea';

export const getClickableRowProps = (onClick: () => void): Omit<ComponentPropsWithoutRef<'tr'>, 'id'> => ({
  className: 'cursor-pointer',
  tabIndex: 0,
  onClick: event => {
    if ((event.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return;
    onClick();
  },
  onKeyDown: event => {
    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  },
});

export const formatFileSize = (bytes: number): string => {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`;
};

export const formatDate = (value?: string | null): string => (value ? new Date(value).toLocaleString() : '');

export type FreaFileGroup = FreaFile & { previousVersions: FreaFile[] };

/** Groups files by `binaryId`; each group is represented by its most recently uploaded file. */
export const groupFilesByBinaryId = (files: FreaFile[]): FreaFileGroup[] => {
  const groups = new Map<string, FreaFile[]>();
  files.forEach(file => {
    const groupKey = file.binaryId || file.id;
    const group = groups.get(groupKey);
    if (group) {
      group.push(file);
    } else {
      groups.set(groupKey, [file]);
    }
  });
  return [...groups.values()].map(group => {
    const [latest, ...previousVersions] = [...group].sort((a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt));
    return { ...latest, previousVersions };
  });
};

/** File groups to list, newest upload first, including deleted uploads. */
export const getFileGroups = (files: FreaFile[]): FreaFileGroup[] =>
  groupFilesByBinaryId(files).sort((a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt));

/** Total size of the groups' latest uploads, excluding deleted ones. */
export const getFileGroupsTotalSize = (groups: FreaFileGroup[]): number =>
  groups.filter(group => group.status !== FreaFileStatus.DELETED).reduce((sum, group) => sum + (group.size || 0), 0);

export const estimateSecondsLeft = (elapsedMs: number, percent: number): number | undefined => {
  if (percent <= 0 || percent >= 100) return undefined;
  return Math.ceil((elapsedMs * (100 - percent)) / percent / 1000);
};

const pluralize = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

export const formatTimeLeft = (seconds: number): string => {
  if (seconds < 60) return `${pluralize(Math.max(1, Math.ceil(seconds)), 'sec')} left`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${pluralize(minutes, 'min')} left`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${pluralize(hours, 'hr')}${remainingMinutes ? ` ${pluralize(remainingMinutes, 'min')}` : ''} left`;
};

// Retention is a rolling per-file age, only for Upload File connections (AWS S3 connections don't have retention).
export const RETENTION_DAYS_OPTIONS: MenuItemData[] = [
  { id: '7', name: '1 week' },
  { id: '14', name: '2 weeks' },
  { id: '21', name: '3 weeks' },
  { id: '28', name: '4 weeks' },
];

// scanner-api `metadata.businessCriticality`: 1 (lowest) to 5 (highest).
export const BUSINESS_CRITICALITY_OPTIONS: MenuItemData[] = [1, 2, 3, 4, 5].map(value => ({ id: String(value), name: String(value) }));

export const toMenuItems = (options: { value: string; title: string }[]): MenuItemData[] =>
  options.map(({ value, title }) => ({ id: value, name: title }));

export const findMenuItem = (items: MenuItemData[], id?: string | number | null) =>
  id == null ? undefined : items.find(item => String(item.id) === String(id));

export const getSelectedId = (value: Parameters<NonNullable<SelectNextProps['onChange']>>[0]): string | undefined => {
  const item = Array.isArray(value) ? value[0] : value;
  return item?.id == null ? undefined : String(item.id);
};

export const DEFAULT_RETENTION_DAYS = 7;

export const formatRetentionDays = (days: number): string => {
  if (days === 7) return '1 week';
  if (days % 7 === 0) return `${days / 7} weeks`;
  return `${days} days`;
};

// ── Upload guards ──────────────────────────────────────────────────────────────
// See ADR "Upload guards (client + server)" — these constants are the client-side
// enforcement; the server applies the same checks as the authoritative layer.

/** Maximum single-file size in bytes (5 GB). */
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 * 1024;

/** Maximum number of files in a single upload session / batch. */
export const MAX_FILES_PER_SESSION = 50;

/** Maximum file name length (characters). Well under S3's 1,024-byte key limit minus the timestamp prefix. */
export const MAX_FILE_NAME_LENGTH = 255;

/** Strips path components and control characters from a file name. */
export const sanitizeFileName = (name: string): string =>
  name
    .replace(/[/\\]/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x7F]/g, '')
    .replace(/\.\.+/g, '')
    .trim();

/** Validates a file name against the upload guard rules. */
export const validateFileName = (name: string): { valid: boolean; error?: string } => {
  const sanitized = sanitizeFileName(name);
  if (!sanitized) return { valid: false, error: 'File name is empty after sanitization' };
  if (sanitized.length > MAX_FILE_NAME_LENGTH) return { valid: false, error: `File name exceeds ${MAX_FILE_NAME_LENGTH} characters` };
  return { valid: true };
};

/** Validates a File object against all upload guard rules. */
export const validateFileForUpload = (file: File): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  if (file.size === 0) {
    errors.push(`File "${file.name}" is empty`);
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    errors.push(`File "${file.name}" exceeds the ${formatFileSize(MAX_FILE_SIZE_BYTES)} size limit`);
  }
  const nameResult = validateFileName(file.name);
  if (!nameResult.valid) {
    errors.push(nameResult.error!);
  }
  return { valid: errors.length === 0, errors };
};

/** Formats a token count for display (e.g. 1,234 or 1.2K). */
export const formatTokenCount = (tokens: number): string => {
  if (tokens < 1000) return tokens.toLocaleString();
  if (tokens < 1_000_000) return `${(tokens / 1000).toFixed(1)}K`;
  return `${(tokens / 1_000_000).toFixed(1)}M`;
};
